import { apiConfig } from '../config/api';
import { Role } from '../types/Role';
import { handleApiError } from '../utils/errorHandler';

interface LoginRequest {
  username: string;
  password: string;
}

interface SignupRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  roles?: Role[];
}

interface AuthResponse {
  token?: string;
  username: string;
  firstName: string;
  lastName: string;
  role?: string;
  roles?: Role[];
  schoolId?: string;
  approvalStatus?: string;
  message?: string;
}

interface ErrorResponse {
  errorCode: string;
  message: string;
  userMessage: string;
  timestamp: string;
  status: number;
  canRetry: boolean;
}

class AuthService {
  // Login user and return authentication data
  async login(username: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${apiConfig.baseURL}/api/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for CORS with authentication
        body: JSON.stringify({
          username,
          password
        } as LoginRequest)
      });

      if (!response.ok) {
        // Clone the response to read it multiple times if needed
        const responseClone = response.clone();

        // Try to parse structured error response
        let errorMessage = 'Login failed';
        try {
          const errorData: ErrorResponse = await response.json();
          errorMessage = errorData.userMessage || errorData.message || errorMessage;
        } catch {
          // Fallback to text response if JSON parsing fails
          try {
            const textError = await responseClone.text();
            errorMessage = textError || errorMessage;
          } catch {
            // Final fallback
            errorMessage = `Login failed with status ${response.status}`;
          }
        }
        throw new Error(errorMessage);
      }

      const data: AuthResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Register new user
  async signup(signupData: SignupRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${apiConfig.baseURL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signupData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Registration failed');
      }

      const data: AuthResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  // Logout user (clear local storage)
  logout(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }

  // Get current user from localStorage
  getCurrentUser(): AuthResponse | null {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return !!user?.token;
  }

  // Get authorization header
  getAuthHeader(): Record<string, string> {
    const user = this.getCurrentUser();
    if (user?.token) {
      return { Authorization: `Bearer ${user.token}` };
    }
    return {};
  }

  // Get token directly
  getToken(): string | null {
    const user = this.getCurrentUser();
    return user?.token || null;
  }

  // Check if current user has a specific role
  hasRole(requiredRole: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.roles) {
      return false;
    }
    return user.roles.includes(requiredRole as Role);
  }
}

export default new AuthService();
